import { AlertTriangle, LoaderCircle, Sparkles } from "lucide-react";

import type { ModelSummaryResponse } from "../../types/api";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";

interface PredictPanelProps {
  disabled: boolean;
  isLoading: boolean;
  message: string | null;
  modelSummary: ModelSummaryResponse | null;
  predictionError: string | null;
  activePreviewUrl: string | null;
  activeSource: "original" | "edited";
  onPredict: () => void;
}

const runtimeTone: Record<string, string> = {
  idle: "Prediction ready",
  loading: "Model warming in background",
  ready: "Model ready",
  error: "Model unavailable"
};

export function PredictPanel({
  disabled,
  isLoading,
  message,
  modelSummary,
  predictionError,
  activePreviewUrl,
  activeSource,
  onPredict
}: PredictPanelProps) {
  const runtimeState = modelSummary?.runtime.warmup_state ?? "idle";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Badge className="border-accent/20 bg-accent/10 text-[#ffd7df]">{runtimeTone[runtimeState]}</Badge>
        <Badge>{modelSummary?.runtime.device.toUpperCase() ?? "CPU"}</Badge>
        <Badge>{activeSource === "edited" ? "Edited candidate" : "Original candidate"}</Badge>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
        <div className="space-y-3 rounded-[26px] border border-white/8 bg-white/[0.03] p-4">
          <p className="text-sm leading-6 text-ink-soft">
            Predict waits for warmup automatically when the runtime is still loading, then sends only the active image
            candidate to the backend.
          </p>
          <Button className="h-12 text-base" disabled={disabled} fullWidth onClick={onPredict}>
            {isLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isLoading ? "Running prediction..." : "Predict"}
          </Button>
          {message ? <p className="text-sm text-accent-strong">{message}</p> : null}
          {predictionError ? (
            <div className="flex items-start gap-3 rounded-2xl border border-[#56222e] bg-[#2a1218] p-3 text-sm text-[#ffd4dc]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{predictionError}</span>
            </div>
          ) : null}
          {runtimeState === "error" && modelSummary?.error ? (
            <p className="text-sm text-[#ffb7c4]">{modelSummary.error}</p>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-[26px] border border-white/8 bg-white/[0.03]">
          {activePreviewUrl ? (
            <img alt="Inference candidate preview" className="h-full max-h-[180px] w-full object-cover" src={activePreviewUrl} />
          ) : (
            <div className="flex h-full min-h-[180px] items-center justify-center text-sm text-ink-muted">No active image</div>
          )}
        </div>
      </div>
    </div>
  );
}
